<template>
  <div class="membershipForm">
    <base-form-field
      ref="membershipCheckboxRef"
      v-model="membership"
      :schema="schema.membership"
      :disabled="disabled"
    >
      <template #label>
        <span v-if="applicantType === 'Primary Applicant'"
          >I am now a <b class="underline">primary member</b> of this credit union.</span
        >
        <span v-else>I am now a member of this credit union</span>
      </template>
    </base-form-field>
    <base-form-field
      v-show="membership"
      v-model="memberId"
      :schema="schema.memberId"
      :disabled="disabled"
    />
  </div>
  <next-button
    v-show="membership"
    class="btn-primary"
    @click="emit('submit', { membership, memberId })"
    :text="`SAVE`"
    :disabled="nextButtonDisabled"
  />
</template>

<script setup lang="ts">
import { reactive, ref, computed } from "vue";
import type { Applicant } from "@/models/application";
import { FormSchema, type FieldSchema } from "@/models/form";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import NextButton from "@/components/shared/NextButton.vue";
import * as yup from "yup";

const props = defineProps<{
  disabled: boolean;
  applicant?: Applicant;
  stipId: number;
}>();

const emit = defineEmits<{
  (
    e: "submit",
    values: {
      membership: undefined | boolean;
      memberId: undefined | string;
    },
  ): void;
}>();

const schema = reactive<FormSchema>({
  membership: {
    name: "membership",
    type: "checkbox",
    label: "",
    required: false,
    hideLabel: true,
  } as FieldSchema,
  memberId: {
    name: "memberId",
    type: "text",
    placeholder: "Account # or Member ID",
    label: "",
    required: true,
    hideLabel: true,
  } as FieldSchema,
});

const membership = ref<boolean | undefined>(undefined);
const memberId = ref<string | undefined>(undefined);

const applicantType = computed<string | undefined>(() => props.applicant?.applicantTypeName);

const nextButtonDisabled = computed<boolean>(() => {
  if (props.disabled || !memberId.value) {
    return true;
  }
  return false;
});

const validationSchema = yup.object({
  membership: yup.boolean().oneOf([true]).required(),
  memberId: yup.string().required(),
});

function setMembershipInfo() {
  if (props.applicant && props.applicant?.membership) {
    membership.value = props.applicant?.membership?.isMember;
    memberId.value = props.applicant?.membership?.memberId;
  }
}

function validateForm() {
  return validationSchema.isValidSync({
    memberId: memberId.value,
    membership: membership.value,
  });
}

defineExpose({
  memberId,
  membership,
  validateForm,
  setMembershipInfo,
});
</script>

<style scoped>
.membershipForm {
  margin-bottom: 6px;
}
.underline {
  text-decoration: underline;
}
</style>
